import React, { useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import { Toaster } from 'react-hot-toast';
import { HelmetProvider } from 'react-helmet-async';
import * as Sentry from '@sentry/react';
import { ExamProvider } from './contexts/ExamContext';
import { UserDataProvider } from './contexts/UserDataContext';
import { TestProvider } from './contexts/TestContext';
import { RealtimeProvider } from './contexts/RealtimeContext';
import { ProctoringProvider } from './components/proctoring/ProctoringProvider';
import { LanguageProvider } from './contexts/LanguageContext';
import ErrorBoundary from './components/ErrorBoundary';
import ProtectedRoute from './components/auth/ProtectedRoute';
import TeacherProtectedRoute from './components/auth/TeacherProtectedRoute';
import RoleBasedRedirect from './components/auth/RoleBasedRedirect';
import DebugUserRole from './components/DebugUserRole';
import PerformanceOptimizer from './components/PerformanceOptimizer';
import NegotiationWidget from './components/negotiation/NegotiationWidget';
import SubdomainRouter from './components/SubdomainRouter';
import cacheManager from './utils/cacheManager';

import ScrollToTop from './components/ScrollToTop';

// Import test utilities (production disabled)
// Development-only imports can be added here if needed
// if (process.env.NODE_ENV === 'development') {
//   import('./utils/testQuestionBankQueries');
// }

// Import all pages directly for reliable routing
import Home from './pages/Home';
import Login from './pages/auth/Login';
import Register from './pages/auth/Register';
import ForgotPassword from './pages/auth/ForgotPassword';
import ResetPassword from './pages/auth/ResetPassword';
// import ResetPassword from './pages/auth/ResetPassword';
import Contact from './pages/Contact';
import Terms from './pages/Terms';
import Privacy from './pages/Privacy';
import ScoreCalculator from './pages/ScoreCalculator';
import SharedBadge from './pages/SharedBadge';
import HobbiesHub from './pages/HobbiesHub';
import JoinActivity from './pages/JoinActivity';

// Import dashboard pages directly
import Dashboard from './pages/dashboard/Dashboard';
import MockTests from './pages/dashboard/MockTests';
import Courses from './pages/dashboard/Courses';
import TestInterface from './pages/dashboard/TestInterface';
import Doubts from './pages/dashboard/Doubts';
import TestLayout from './components/TestLayout';
import TestResults from './pages/dashboard/TestResults';
import TestResultsList from './pages/dashboard/TestResultsList';
import Resources from './pages/dashboard/Resources';
import Revision from './pages/dashboard/Revision';
import Notes from './pages/dashboard/Notes';
import HelpSupport from './pages/dashboard/HelpSupport';
import LockedNotes from './components/LockedNotes';
import Settings from './pages/dashboard/Settings';
import Vacancies from './pages/dashboard/Vacancies';
import MyApplications from './pages/dashboard/MyApplications';
import StudentAnalysis from './pages/dashboard/StudentAnalysis';

// Import admin pages directly (all optimized versions)
import AdminDashboard from './pages/admin/AdminDashboardOptimized';
import ExamManagement from './pages/admin/ExamManagementOptimized';
import LiveClassManagement from './pages/admin/LiveClassManagementOptimized';
import TestManagement from './pages/admin/TestManagementOptimized';
import CourseManagement from './pages/admin/CourseManagementOptimized';
import ResourceManagement from './pages/admin/ResourceManagementOptimized';
import UserManagement from './pages/admin/UserManagementOptimized';
import AdminAnalytics from './pages/admin/AdminAnalyticsOptimized';
import VacancyManagement from './pages/admin/VacancyManagement';
import DataMigration from './components/admin/DataMigration';
import AdminAccess from './components/admin/AdminAccess';
import AdminProctoringDashboard from './components/proctoring/AdminProctoringDashboard';
import FcmBootstrap from './components/notifications/FcmBootstrap';

// Import founder pages
import FounderDashboard from './pages/founder/FounderDashboard';
import AdminManagement from './pages/founder/AdminManagement';
import AdminLogsViewer from './pages/founder/AdminLogsViewer';


import PerformanceMonitor from './components/admin/PerformanceMonitor';
import HelpRequestsManagement from './pages/admin/HelpRequestsManagementOptimized';
import AdminDoubtsManagement from './pages/admin/AdminDoubtsManagementOptimized';
import SupportTicketsManagement from './pages/admin/SupportTicketsManagementOptimized';
import EducationalSupportRequests from './pages/admin/EducationalSupportRequests';
import SystemSettings from './pages/admin/SystemSettings';
import StatsManager from './pages/admin/StatsManager';
import PollManagement from './pages/admin/PollManagement';
import ActivityRegistrationManagement from './pages/admin/ActivityRegistrationManagement';
import LeaderboardManagement from './pages/admin/LeaderboardManagementOptimized';
import TuitionTeacherManagement from './pages/admin/TuitionTeacherManagementOptimized';
import CoachingManagement from './pages/admin/CoachingManagementOptimized';
import CoachingAdminPermissions from './pages/admin/CoachingAdminPermissions';
import TeacherRequestsManagement from './pages/admin/TeacherRequestsManagement';
import TeamManagement from './pages/admin/TeamManagement';
import TimetableManagement from './pages/admin/TimetableManagement';
import ResultsAdmitCardManagement from './pages/admin/ResultsAdmitCardManagement';
import FirebaseIndexHelper from './components/admin/FirebaseIndexHelper';
import AIKnowledgeManagement from './pages/admin/AIKnowledgeManagement';
import BlogManagement from './pages/admin/BlogManagementOptimized';
import UnschoolingManagement from './pages/admin/UnschoolingManagementOptimized';
import BlogList from './pages/BlogList';
import BlogDetail from './pages/BlogDetail';

// Import study pages directly
import Flashcards from './pages/study/Flashcards';
import PracticeSets from './pages/study/PracticeSets';
import SavedQuestions from './pages/dashboard/SavedQuestions';
import Bookmarks from './pages/dashboard/Bookmarks';
import LiveClasses from './pages/dashboard/LiveClasses';
import LiveClassView from './pages/liveclass/LiveClassView';

// Import PDF Scanner pages
import PDFScanner from './pages/PDFScanner';
import TestPDFScanner from './pages/TestPDFScanner';

// Import Tuition Teachers page
import TuitionTeachers from './pages/TuitionTeachers';
import TeacherDashboardPage from './pages/TeacherDashboard';
import TeacherBatchManagement from './components/TeacherBatchManagement';

// Import new teacher pages
import TeacherDashboard from './pages/teacher/TeacherDashboard';
import TeacherBatchManagementPage from './pages/teacher/TeacherBatchManagement';
import TeacherLeaveManagement from './pages/teacher/TeacherLeaveManagement';
import TeacherSalary from './pages/teacher/TeacherSalary';
import TeacherProfile from './pages/teacher/TeacherProfile';
import TeacherBatches from './pages/teacher/TeacherBatches';
import TeacherSettings from './pages/teacher/TeacherSettings';
import TeacherPlaceholder from './pages/teacher/TeacherPlaceholder';
import TeacherMyAttendance from './pages/teacher/TeacherMyAttendance';


import TeacherLayout from './components/layout/TeacherLayout';
import TeacherSettlements from './components/teacher/TeacherSettlements';
import CoachingTeacherManagement from './pages/admin/CoachingTeacherManagement';
import TeacherSettlementsPage from './pages/coaching/TeacherSettlementsPage';
import StudentManagementPage from './pages/admin/StudentManagementPage';
import StudentInfoPage from './pages/admin/StudentInfoPage';

// Import institute pages
import InstituteDashboard from './pages/institute/InstituteDashboard';
import BatchSchedulePage from './pages/batch/BatchSchedulePage';
import BatchClassView from './pages/batch/BatchClassView';
import BatchManagement from './pages/admin/BatchManagementOptimized';
import BatchDetailsPage from './pages/admin/BatchDetailsPage';
import AttendanceManagement from './pages/admin/AttendanceManagementOptimized';
import TransportManagement from './pages/admin/TransportManagementOptimized';
import ActivitiesManagement from './pages/admin/ActivitiesManagementOptimized';
import GrievanceManagement from './pages/admin/GrievanceManagementOptimized';
import LeaveManagement from './pages/admin/LeaveManagementOptimized';
import CoachingLeaveManagement from './pages/coaching/CoachingLeaveManagement';
import WhatsAppMessagingPage from './pages/coaching/WhatsAppMessagingPage';
import DataAnalytics from './pages/coaching/DataAnalytics';

// Coaching pages
import CoachingSignup from './pages/CoachingSignup';
import CoachingDashboard from './pages/coaching/CoachingDashboard';
import CoachingAdminDashboard from './pages/admin/CoachingAdminDashboard';
import SubscriptionManagement from './pages/admin/SubscriptionManagementOptimized';
import FeeManagement from './pages/admin/FeeManagementOptimized';
import ReportCards from './pages/admin/ReportCardsOptimized';
import IDCardManagement from './pages/admin/IDCardManagementOptimized';
import CoachingAdminLayout from './components/layout/CoachingAdminLayout';
import CoachingProtectedRoute from './components/auth/CoachingProtectedRoute';
import CoachingStaffManagement from './pages/admin/CoachingStaffManagement';
import StoreManagementPage from './pages/admin/StoreManagementPage';
import HostelManagement from './pages/admin/hostel/HostelManagement';

// Admissions pages
import Admissions from './pages/Admissions';
import InstitutionLandingPage from './pages/InstitutionLandingPage';
import InstitutionCustomization from './pages/admin/InstitutionCustomization';
import InstitutionManagement from './pages/admin/InstitutionManagement';
import InstituteAccountCreation from './pages/admin/InstituteAccountCreation';
import AllApplications from './pages/admin/AllApplications';
import AllInstitutions from './pages/admin/AllInstitutions';
import Refunds from './pages/admin/Refunds';
import Settlements from './pages/admin/SettlementsOptimized';
import InterviewManagement from './pages/admin/InterviewManagement';
import CreateInterview from './pages/admin/CreateInterview';
import AddCandidate from './pages/admin/AddCandidate';
import InterviewReport from './pages/admin/InterviewReport';

// Unschooling Mode pages
// import UnschoolingHub from './pages/unschooling/UnschoolingHub';
// import ExploreSpace from './pages/unschooling/ExploreSpace';
// import ContentView from './pages/unschooling/ContentView';
// import ProgressReport from './pages/unschooling/ProgressReport';
import ComingSoon from './pages/ComingSoon';

// Admissions Management pages (for coaching admins)
import AdmissionsDashboard from './pages/admin/admissions/AdmissionsDashboard';
import AdmissionsProfile from './pages/admin/admissions/AdmissionsProfile';
import ApplicationFormBuilder from './pages/admin/admissions/ApplicationFormBuilder';
import CurriculumManagement from './pages/admin/admissions/CurriculumManagement';
import MaterialsManagement from './pages/admin/admissions/MaterialsManagement';
import FacultyManagement from './pages/admin/admissions/FacultyManagement';
import ViewApplications from './pages/admin/admissions/ViewApplications';
import FeeStructureManagement from './pages/admin/admissions/FeeStructureManagement';
import ResultsManagement from './pages/admin/admissions/ResultsManagement';
import AdmissionTestsList from './pages/admin/admissions/AdmissionTestsList';
import AdmissionTestEdit from './pages/admin/admissions/AdmissionTestEdit';
import MigrateTestData from './pages/admin/admissions/MigrateTestData';

// Components - Direct imports
import Layout from './components/layout/Layout';
import AdminLayout from './components/layout/AdminLayout';
import FounderLayout from './components/layout/FounderLayout';

// Interview pages
import InterviewInterface from './pages/InterviewInterface';

// AI Educator
import AIEducator from './pages/AIEducator';



function App() {
  // Initialize cache management
  useEffect(() => {
    // Check for updates on app start
    cacheManager.checkForUpdates();
    
    // Set up periodic update checks (every 5 minutes)
    const updateInterval = setInterval(() => {
      cacheManager.checkForUpdates();
    }, 5 * 60 * 1000);
    
    return () => clearInterval(updateInterval);
  }, []);

  // Use Sentry's ErrorBoundary if DSN is configured, otherwise use custom one
  const SentryErrorBoundary = process.env.REACT_APP_SENTRY_DSN 
    ? Sentry.ErrorBoundary 
    : ErrorBoundary;

  return (
    <SentryErrorBoundary
      fallback={({ error, resetError }) => (
        <ErrorBoundary>
          <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center p-4">
            <div className="max-w-md w-full bg-white dark:bg-gray-800 rounded-lg shadow-lg p-6">
              <div className="text-center">
                <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
                  Oops! Something went wrong
                </h3>
                <p className="text-sm text-gray-600 dark:text-gray-400 mb-6">
                  We're sorry, but something unexpected happened. Our team has been notified.
                </p>
                <button
                  onClick={resetError}
                  className="w-full px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                >
                  Try Again
                </button>
              </div>
            </div>
          </div>
        </ErrorBoundary>
      )}
      showDialog={false}
    >
      <ErrorBoundary>
        <HelmetProvider>
          <LanguageProvider>
            <Router>
              <ScrollToTop />
              <UserDataProvider>
                <ExamProvider>
                  <TestProvider>
                    <RealtimeProvider>
                      <FcmBootstrap />
                        <ProctoringProvider>
                        <div className="App">
                        <PerformanceOptimizer />
                        <Toaster
                          position="top-right"
                          toastOptions={{
                          duration: 4000,
                          style: {
                            background: '#363636',
                            color: '#fff',
                          },
                        }}
                      />
              
                      <RoleBasedRedirect>
                        <DebugUserRole />
                        <SubdomainRouter>
                        <Routes>
                  {/* Public Routes */}
                  <Route path="/" element={<Home />} />
                  <Route path="/login" element={<Login />} />
                  <Route path="/register" element={<Register />} />
                  <Route path="/forgot-password" element={<ForgotPassword />} />
                  <Route path="/reset-password" element={<ResetPassword />} />
                  <Route path="/contact" element={<Contact />} />
                  <Route path="/terms" element={<Terms />} />
                  <Route path="/privacy" element={<Privacy />} />
                  <Route path="/score-calculator" element={<ScoreCalculator />} />
                  <Route path="/badge/:badgeData" element={<SharedBadge />} />
                  <Route path="/hobbies" element={<HobbiesHub />} />
                  <Route path="/join-activity/:registrationId" element={<JoinActivity />} />
                  <Route path="/coaching-signup" element={<CoachingSignup />} />
                  <Route path="/admissions" element={<Admissions />} />
                  <Route path="/admissions/:id" element={<InstitutionLandingPage />} />
                  <Route path="/institute/:id" element={<InstitutionLandingPage />} />
                  <Route path="/blog" element={<BlogList />} />
                  <Route path="/blog/:slug" element={<BlogDetail />} />
                  
                  {/* Public Interview Route */}
                  <Route path="/interview/:interviewId/:candidateId" element={<InterviewInterface />} />
                  
                  {/* AI Educator Route - Moved to dashboard */}
                  {/* <Route path="/ai-educator" element={
                      <ProtectedRoute>
                        <AIEducator />
                      </ProtectedRoute>
                  } /> */}

                  
                  {/* Admin Access Portal - Protected with authentication */}
                  <Route path="/admin-portal" element={
                      <ProtectedRoute requireAdmin={false}>
                        <AdminAccess />
                      </ProtectedRoute>
                  } />
                  
                  {/* Admin Routes - Protected with admin access */}
                   <Route path="/admin" element={
                       <ProtectedRoute requireAdmin={true}>
                         <AdminLayout>
                           <AdminDashboard />
                         </AdminLayout>
                       </ProtectedRoute>
                   } />
                   
                   <Route path="/admin/exam-management" element={
                       <ProtectedRoute requireAdmin={true}>
                         <AdminLayout>
                           <ExamManagement />
                         </AdminLayout>
                       </ProtectedRoute>
                   } />
                   
                   <Route path="/admin/live-class-management" element={
                       <ProtectedRoute requireAdmin={true}>
                         <AdminLayout>
                           <LiveClassManagement />
                         </AdminLayout>
                       </ProtectedRoute>
                   } />
                   
                   <Route path="/admin/test-management" element={
                       <ProtectedRoute requireAdmin={true}>
                         <AdminLayout>
                           <TestManagement />
                         </AdminLayout>
                       </ProtectedRoute>
                   } />
                   
                   <Route path="/admin/course-management" element={
                       <ProtectedRoute requireAdmin={true}>
                         <AdminLayout>
                           <CourseManagement />
                         </AdminLayout>
                       </ProtectedRoute>
                   } />
                   
                   <Route path="/admin/resource-management" element={
                       <ProtectedRoute requireAdmin={true}>
                         <AdminLayout>
                           <ResourceManagement />
                         </AdminLayout>
                       </ProtectedRoute>
                   } />
                   
                   <Route path="/admin/user-management" element={
                       <ProtectedRoute requireAdmin={true}>
                         <AdminLayout>
                           <UserManagement />
                         </AdminLayout>
                       </ProtectedRoute>
                   } />
                   
                   <Route path="/admin/tuition-teachers" element={
                       <ProtectedRoute requireAdmin={true}>
                         <AdminLayout>
                           <TuitionTeacherManagement />
                         </AdminLayout>
                       </ProtectedRoute>
                   } />
                   
                   <Route path="/admin/coaching-management" element={
                       <ProtectedRoute requireAdmin={true}>
                         <AdminLayout>
                           <CoachingManagement />
                         </AdminLayout>
                       </ProtectedRoute>
                   } />
                   
                   <Route path="/admin/coaching-admin-permissions" element={
                       <ProtectedRoute requireAdmin={true}>
                         <AdminLayout>
                           <CoachingAdminPermissions />
                         </AdminLayout>
                       </ProtectedRoute>
                   } />
                   
                   <Route path="/admin/teacher-requests" element={
                       <ProtectedRoute requireAdmin={true}>
                         <AdminLayout>
                           <TeacherRequestsManagement />
                         </AdminLayout>
                       </ProtectedRoute>
                   } />
                   
                   <Route path="/admin/analytics" element={
                       <ProtectedRoute requireAdmin={true}>
                         <AdminLayout>
                           <AdminAnalytics />
                         </AdminLayout>
                       </ProtectedRoute>
                   } />
                   
                   <Route path="/admin/vacancy-management" element={
                       <ProtectedRoute requireAdmin={true}>
                         <AdminLayout>
                           <VacancyManagement />
                         </AdminLayout>
                       </ProtectedRoute>
                   } />
                   
                   <Route path="/admin/batch-management" element={
                       <ProtectedRoute requireAdmin={true}>
                         <AdminLayout>
                           <BatchManagement />
                         </AdminLayout>
                       </ProtectedRoute>
                   } />
                   
                   <Route path="/admin/data-migration" element={
                       <ProtectedRoute requireAdmin={true}>
                         <AdminLayout>
                           <DataMigration />
                         </AdminLayout>
                       </ProtectedRoute>
                   } />
                   

                   
                   <Route path="/admin/performance-monitor" element={
                       <ProtectedRoute requireAdmin={true}>
                         <AdminLayout>
                           <PerformanceMonitor />
                         </AdminLayout>
                       </ProtectedRoute>
                   } />
                   
                   <Route path="/admin/help-requests" element={
                       <ProtectedRoute requireAdmin={true}>
                         <AdminLayout>
                           <HelpRequestsManagement />
                         </AdminLayout>
                       </ProtectedRoute>
                   } />
                   
                   <Route path="/admin/support-tickets" element={
                       <ProtectedRoute requireAdmin={true}>
                         <AdminLayout>
                           <SupportTicketsManagement />
                         </AdminLayout>
                       </ProtectedRoute>
                   } />
                   
                   <Route path="/admin/educational-support" element={
                       <ProtectedRoute requireAdmin={true}>
                         <AdminLayout>
                           <EducationalSupportRequests />
                         </AdminLayout>
                       </ProtectedRoute>
                   } />
                   
                   <Route path="/admin/doubts-management" element={
                       <ProtectedRoute requireAdmin={true}>
                         <AdminLayout>
                           <AdminDoubtsManagement />
                         </AdminLayout>
                       </ProtectedRoute>
                   } />
                   
                  <Route path="/admin/system-settings" element={
                      <ProtectedRoute requireAdmin={true}>
                        <AdminLayout>
                          <SystemSettings />
                        </AdminLayout>
                      </ProtectedRoute>
                    } />
                    
                  <Route path="/admin/stats-manager" element={
                      <ProtectedRoute requireAdmin={true}>
                        <AdminLayout>
                          <StatsManager />
                        </AdminLayout>
                      </ProtectedRoute>
                    } />
                    
                  <Route path="/admin/poll-management" element={
                      <ProtectedRoute requireAdmin={true}>
                        <AdminLayout>
                          <PollManagement />
                        </AdminLayout>
                      </ProtectedRoute>
                    } />
                    
                  <Route path="/admin/activity-registration" element={
                      <ProtectedRoute requireAdmin={true}>
                        <AdminLayout>
                          <ActivityRegistrationManagement />
                        </AdminLayout>
                      </ProtectedRoute>
                    } />
                    
                  <Route path="/admin/leaderboard-management" element={
                      <ProtectedRoute requireAdmin={true}>
                        <AdminLayout>
                          <LeaderboardManagement />
                        </AdminLayout>
                      </ProtectedRoute>
                    } />
                   
                   
                  <Route path="/admin/firebase-index-helper" element={
                      <ProtectedRoute requireAdmin={true}>
                        <AdminLayout>
                          <FirebaseIndexHelper />
                        </AdminLayout>
                      </ProtectedRoute>
                  } />
                  
                  <Route path="/admin/ai-knowledge" element={
                      <ProtectedRoute requireAdmin={true}>
                        <AdminLayout>
                          <AIKnowledgeManagement />
                        </AdminLayout>
                      </ProtectedRoute>
                  } />
                  
                  <Route path="/admin/blog-management" element={
                      <ProtectedRoute requireAdmin={true}>
                        <AdminLayout>
                          <BlogManagement />
                        </AdminLayout>
                      </ProtectedRoute>
                  } />
                  
                  <Route path="/admin/unschooling-management" element={
                      <ProtectedRoute requireAdmin={true}>
                        <AdminLayout>
                          <UnschoolingManagement />
                        </AdminLayout>
                      </ProtectedRoute>
                  } />
                  
                  <Route path="/admin/team-management" element={
                      <ProtectedRoute requireAdmin={true}>
                        <AdminLayout>
                          <TeamManagement />
                        </AdminLayout>
                      </ProtectedRoute>
                  } />
                  
                  <Route path="/admin/timetable-management" element={
                      <ProtectedRoute requireAdmin={true}>
                        <AdminLayout>
                          <TimetableManagement />
                        </AdminLayout>
                      </ProtectedRoute>
                  } />
                  
                  <Route path="/admin/results-admit-card-management" element={
                      <ProtectedRoute requireAdmin={true}>
                        <AdminLayout>
                          <ResultsAdmitCardManagement />
                        </AdminLayout>
                      </ProtectedRoute>
                  } />
                  
                  <Route path="/admin/institution-customization" element={
                      <ProtectedRoute requireAdmin={true}>
                        <AdminLayout>
                          <InstitutionCustomization />
                        </AdminLayout>
                      </ProtectedRoute>
                  } />
                  
                  <Route path="/admin/institution-management" element={
                      <ProtectedRoute requireAdmin={true}>
                        <AdminLayout>
                          <InstitutionManagement />
                        </AdminLayout>
                      </ProtectedRoute>
                  } />
                  
                  <Route path="/admin/create-institute-account" element={
                      <ProtectedRoute requireAdmin={true}>
                        <AdminLayout>
                          <InstituteAccountCreation />
                        </AdminLayout>
                      </ProtectedRoute>
                  } />
                  
                  <Route path="/admin/all-applications" element={
                      <ProtectedRoute requireAdmin={true}>
                        <AdminLayout>
                          <AllApplications />
                        </AdminLayout>
                      </ProtectedRoute>
                  } />
                  
                  <Route path="/admin/all-institutions" element={
                      <ProtectedRoute requireAdmin={true}>
                        <AdminLayout>
                          <AllInstitutions />
                        </AdminLayout>
                      </ProtectedRoute>
                  } />
                  
                  <Route path="/admin/refunds" element={
                      <ProtectedRoute requireAdmin={true}>
                        <AdminLayout>
                          <Refunds />
                        </AdminLayout>
                      </ProtectedRoute>
                  } />
                  
                  <Route path="/admin/settlements" element={
                      <ProtectedRoute requireAdmin={true}>
                        <AdminLayout>
                          <Settlements />
                        </AdminLayout>
                      </ProtectedRoute>
                  } />
                  
                  <Route path="/admin/interview-management" element={
                      <ProtectedRoute requireAdmin={true}>
                        <AdminLayout>
                          <InterviewManagement />
                        </AdminLayout>
                      </ProtectedRoute>
                  } />
                  
                  <Route path="/admin/interview-management/create" element={
                      <ProtectedRoute requireAdmin={true}>
                        <AdminLayout>
                          <CreateInterview />
                        </AdminLayout>
                      </ProtectedRoute>
                  } />
                  
                  <Route path="/admin/interview-management/add-candidate/:interviewId" element={
                      <ProtectedRoute requireAdmin={true}>
                        <AdminLayout>
                          <AddCandidate />
                        </AdminLayout>
                      </ProtectedRoute>
                  } />
                  
                  <Route path="/admin/interview-management/report/:interviewId/:candidateId" element={
                      <ProtectedRoute requireAdmin={true}>
                        <AdminLayout>
                          <InterviewReport />
                        </AdminLayout>
                      </ProtectedRoute>
                  } />
                   
                  <Route path="/admin/proctoring" element={
                      <ProtectedRoute requireAdmin={true}>
                        <AdminLayout>
                          <AdminProctoringDashboard />
                        </AdminLayout>
                      </ProtectedRoute>
                  } />
                  
                  {/* Founder Routes - Protected with founder access */}
                  <Route path="/founder" element={
                      <ProtectedRoute requireAdmin={true}>
                        <FounderLayout>
                          <FounderDashboard />
                        </FounderLayout>
                      </ProtectedRoute>
                  } />
                  
                  <Route path="/founder/admin-management" element={
                      <ProtectedRoute requireAdmin={true}>
                        <FounderLayout>
                          <AdminManagement />
                        </FounderLayout>
                      </ProtectedRoute>
                  } />
                  
                  <Route path="/founder/admin-logs" element={
                      <ProtectedRoute requireAdmin={true}>
                        <FounderLayout>
                          <AdminLogsViewer />
                        </FounderLayout>
                      </ProtectedRoute>
                  } />

                   
                  {/* Protected Dashboard Routes */}
                  <Route path="/dashboard" element={
                      <ProtectedRoute>
                        <Layout>
                          <Dashboard />
                        </Layout>
                      </ProtectedRoute>
                  } />
                  
                  <Route path="/batch/:batchId" element={
                      <ProtectedRoute>
                        <Layout>
                          <BatchSchedulePage />
                        </Layout>
                      </ProtectedRoute>
                  } />
                  
                  <Route path="/batch-class/:classId" element={
                      <ProtectedRoute>
                        <Layout>
                          <BatchClassView />
                        </Layout>
                      </ProtectedRoute>
                  } />
                  
                  <Route path="/dashboard/mock-tests" element={
                      <ProtectedRoute>
                        <Layout>
                          <MockTests />
                        </Layout>
                      </ProtectedRoute>
                  } />
                  
                  <Route path="/dashboard/courses" element={
                      <ProtectedRoute>
                        <Layout>
                          <Courses />
                        </Layout>
                      </ProtectedRoute>
                  } />
                  
                  <Route path="/dashboard/test/:testId" element={
                      <ProtectedRoute>
                        <TestInterface />
                      </ProtectedRoute>
                  } />
                  
                  <Route path="/dashboard/test-layout/:testId" element={
                      <ProtectedRoute>
                        <TestLayout />
                      </ProtectedRoute>
                  } />
                  
                  <Route path="/dashboard/doubts" element={
                      <ProtectedRoute>
                        <Layout>
                          <Doubts />
                        </Layout>
                      </ProtectedRoute>
                  } />
                  
                  <Route path="/dashboard/test-results" element={
                     <ProtectedRoute>
                       <Layout>
                         <TestResultsList />
                       </Layout>
                     </ProtectedRoute>
                   } />
                  
                  <Route path="/dashboard/test-results/:testId" element={
                    <ProtectedRoute>
                      <Layout>
                        <TestResults />
                      </Layout>
                    </ProtectedRoute>
                  } />
                  
                  <Route path="/dashboard/test-results-list" element={
                    <ProtectedRoute>
                      <Layout>
                        <TestResultsList />
                      </Layout>
                    </ProtectedRoute>
                  } />
                  
                  <Route path="/dashboard/resources" element={
                      <ProtectedRoute>
                        <Layout>
                          <Resources />
                        </Layout>
                      </ProtectedRoute>
                  } />
                  
                  <Route path="/dashboard/revision" element={
                      <ProtectedRoute>
                        <Layout>
                          <Revision />
                        </Layout>
                      </ProtectedRoute>
                  } />
                  
                  <Route path="/dashboard/help-support" element={
                      <ProtectedRoute>
                        <Layout>
                          <HelpSupport />
                        </Layout>
                      </ProtectedRoute>
                  } />
                  
                  <Route path="/dashboard/notes" element={
                      <ProtectedRoute>
                        <Layout>
                          <LockedNotes />
                        </Layout>
                      </ProtectedRoute>
                  } />
                  
                  <Route path="/dashboard/settings" element={
                      <ProtectedRoute>
                        <Layout>
                          <Settings />
                        </Layout>
                      </ProtectedRoute>
                  } />
                  
                  <Route path="/dashboard/analysis" element={
                      <ProtectedRoute>
                        <StudentAnalysis />
                      </ProtectedRoute>
                  } />
                  
                  <Route path="/dashboard/vacancies" element={
                      <ProtectedRoute>
                        <Layout>
                          <Vacancies />
                        </Layout>
                      </ProtectedRoute>
                  } />
                  
                  <Route path="/dashboard/my-applications" element={
                      <ProtectedRoute>
                        <Layout>
                          <MyApplications />
                        </Layout>
                      </ProtectedRoute>
                  } />
                  
                  <Route path="/dashboard/saved-questions" element={
                      <ProtectedRoute>
                        <Layout>
                          <SavedQuestions />
                        </Layout>
                      </ProtectedRoute>
                  } />
                  
                  <Route path="/dashboard/bookmarks" element={
                      <ProtectedRoute>
                        <Layout>
                          <Bookmarks />
                        </Layout>
                      </ProtectedRoute>
                  } />
                  
                  <Route path="/dashboard/live-classes" element={
                      <ProtectedRoute>
                        <Layout>
                          <LiveClasses />
                        </Layout>
                      </ProtectedRoute>
                  } />
                  
                  <Route path="/dashboard/ai-educator" element={
                      <ProtectedRoute>
                        <AIEducator />
                      </ProtectedRoute>
                  } />
                  
                  <Route path="/live-class/:classId" element={
                      <ProtectedRoute>
                        <LiveClassView />
                      </ProtectedRoute>
                  } />
                  
                  
                  {/* Study Routes */}
                  <Route path="/study/flashcards" element={
                      <ProtectedRoute>
                        <Layout>
                          <Flashcards />
                        </Layout>
                      </ProtectedRoute>
                  } />
                  
                  <Route path="/study/practice-sets" element={
                      <ProtectedRoute>
                        <Layout>
                          <PracticeSets />
                        </Layout>
                      </ProtectedRoute>
                  } />
                  
                  {/* PDF Scanner Routes */}
                  <Route path="/pdf-scanner" element={
                      <ProtectedRoute>
                        <Layout>
                          <PDFScanner />
                        </Layout>
                      </ProtectedRoute>
                  } />
                  
                  <Route path="/test-pdf-scanner" element={
                      <ProtectedRoute>
                        <Layout>
                          <TestPDFScanner />
                        </Layout>
                      </ProtectedRoute>
                  } />
                  
                  {/* Tuition Teachers Routes */}
                  <Route path="/tuition-teachers" element={
                      <ProtectedRoute>
                        <Layout>
                          <TuitionTeachers />
                        </Layout>
                      </ProtectedRoute>
                  } />
                  
                  {/* Teacher Dashboard Route */}
                  <Route path="/teacher-dashboard" element={
                      <TeacherProtectedRoute>
                        <Layout>
                          <TeacherDashboardPage />
                        </Layout>
                      </TeacherProtectedRoute>
                  } />
                  
                  {/* Teacher Batch Management Route */}
                  <Route path="/teacher-batch-management" element={
                      <TeacherProtectedRoute>
                        <Layout>
                          <TeacherBatchManagement />
                        </Layout>
                      </TeacherProtectedRoute>
                  } />
                  
                  {/* New Teacher Routes */}
                  <Route path="/teacher/dashboard" element={
                      <ProtectedRoute>
                        <TeacherDashboard />
                      </ProtectedRoute>
                  } />
                  
                  <Route path="/teacher/batch/:batchId" element={
                      <ProtectedRoute>
                        <TeacherLayout>
                          <TeacherBatchManagementPage />
                        </TeacherLayout>
                      </ProtectedRoute>
                  } />
                  
                  <Route path="/teacher/leave" element={
                      <ProtectedRoute>
                        <TeacherLayout>
                          <TeacherLeaveManagement />
                        </TeacherLayout>
                      </ProtectedRoute>
                  } />
                  
                  <Route path="/teacher/salary" element={
                      <ProtectedRoute>
                        <TeacherLayout>
                          <TeacherSalary />
                        </TeacherLayout>
                      </ProtectedRoute>
                  } />
                  
                  <Route path="/teacher/profile" element={
                      <ProtectedRoute>
                        <TeacherLayout>
                          <TeacherProfile />
                        </TeacherLayout>
                      </ProtectedRoute>
                  } />
                  
                  <Route path="/teacher/batches" element={
                      <ProtectedRoute>
                        <TeacherBatches />
                      </ProtectedRoute>
                  } />
                  
                  <Route path="/teacher/settings" element={
                      <ProtectedRoute>
                        <TeacherSettings />
                      </ProtectedRoute>
                  } />
                  

                  

                  

                  
                  <Route path="/teacher/my-attendance" element={
                      <ProtectedRoute>
                        <TeacherLayout>
                          <TeacherMyAttendance />
                        </TeacherLayout>
                      </ProtectedRoute>
                  } />
                  
                  <Route path="/teacher/settlements" element={
                      <ProtectedRoute>
                        <TeacherLayout>
                          <TeacherSettlements />
                        </TeacherLayout>
                      </ProtectedRoute>
                  } />
                  
                  <Route path="/teacher/invoices" element={
                      <ProtectedRoute>
                        <TeacherLayout>
                          <TeacherPlaceholder 
                            title="Invoices" 
                            description="Download your invoices and receipts"
                          />
                        </TeacherLayout>
                      </ProtectedRoute>
                  } />
                  
                  {/* Institute Routes */}
                  <Route path="/institute/dashboard" element={
                      <ProtectedRoute>
                        <Layout>
                          <InstituteDashboard />
                        </Layout>
                      </ProtectedRoute>
                  } />
                  
                  {/* Coaching Dashboard Routes */}
                  <Route path="/coaching/:coachingId" element={
                      <ProtectedRoute>
                        <CoachingDashboard />
                      </ProtectedRoute>
                  } />
                  
                  <Route path="/coaching/:coachingId/actions" element={
                      <ProtectedRoute>
                        <CoachingDashboard />
                      </ProtectedRoute>
                  } />
                  
                  <Route path="/coaching/:coachingId/teachers" element={
                      <ProtectedRoute>
                        <CoachingDashboard />
                      </ProtectedRoute>
                  } />
                  
                  <Route path="/coaching/:coachingId/batches" element={
                      <ProtectedRoute>
                        <CoachingDashboard />
                      </ProtectedRoute>
                  } />
                  
                  {/* Coaching Admin Routes */}
                  <Route path="/coaching-admin" element={
                      <CoachingProtectedRoute>
                        <CoachingAdminLayout>
                          <CoachingAdminDashboard />
                        </CoachingAdminLayout>
                      </CoachingProtectedRoute>
                  } />
                  
                  <Route path="/coaching-admin/tests" element={
                      <CoachingProtectedRoute>
                        <CoachingAdminLayout>
                          <TestManagement />
                        </CoachingAdminLayout>
                      </CoachingProtectedRoute>
                  } />
                  
                  <Route path="/coaching-admin/exams" element={
                      <CoachingProtectedRoute>
                        <CoachingAdminLayout>
                          <ExamManagement />
                        </CoachingAdminLayout>
                      </CoachingProtectedRoute>
                  } />
                  
                  <Route path="/coaching-admin/resources" element={
                      <CoachingProtectedRoute>
                        <CoachingAdminLayout>
                          <ResourceManagement />
                        </CoachingAdminLayout>
                      </CoachingProtectedRoute>
                  } />
                  
                  <Route path="/coaching-admin/batches" element={
                      <CoachingProtectedRoute>
                        <CoachingAdminLayout>
                          <BatchManagement />
                        </CoachingAdminLayout>
                      </CoachingProtectedRoute>
                  } />
                  
                  <Route path="/coaching-admin/batches/:batchId" element={
                      <CoachingProtectedRoute>
                        <CoachingAdminLayout>
                          <BatchDetailsPage />
                        </CoachingAdminLayout>
                      </CoachingProtectedRoute>
                  } />
                  
                  <Route path="/coaching-admin/teachers" element={
                      <CoachingProtectedRoute>
                        <CoachingAdminLayout>
                          <CoachingTeacherManagement />
                        </CoachingAdminLayout>
                      </CoachingProtectedRoute>
                  } />
                  
<Route path="/coaching-admin/students" element={
    <CoachingProtectedRoute>
      <CoachingAdminLayout>
        <StudentManagementPage />
      </CoachingAdminLayout>
    </CoachingProtectedRoute>
} />
<Route path="/coaching-admin/student-info" element={
    <CoachingProtectedRoute>
      <CoachingAdminLayout>
        <StudentInfoPage />
      </CoachingAdminLayout>
    </CoachingProtectedRoute>
} />

                  <Route path="/coaching-admin/whatsapp" element={
                      <CoachingProtectedRoute>
                        <CoachingAdminLayout>
                          <WhatsAppMessagingPage />
                        </CoachingAdminLayout>
                      </CoachingProtectedRoute>
                  } />
                  
                  <Route path="/coaching-admin/settlements" element={
                      <CoachingProtectedRoute>
                        <CoachingAdminLayout>
                          <TeacherSettlementsPage />
                        </CoachingAdminLayout>
                      </CoachingProtectedRoute>
                  } />
                  
                  <Route path="/coaching-admin/attendance" element={
                      <CoachingProtectedRoute>
                        <CoachingAdminLayout>
                          <AttendanceManagement />
                        </CoachingAdminLayout>
                      </CoachingProtectedRoute>
                  } />
                  <Route path="/coaching-admin/transport" element={
                      <CoachingProtectedRoute>
                        <CoachingAdminLayout>
                          <TransportManagement />
                        </CoachingAdminLayout>
                      </CoachingProtectedRoute>
                  } />
                  <Route path="/coaching-admin/activities" element={
                      <CoachingProtectedRoute>
                        <CoachingAdminLayout>
                          <ActivitiesManagement />
                        </CoachingAdminLayout>
                      </CoachingProtectedRoute>
                  } />
                  <Route path="/coaching-admin/grievance" element={
                      <CoachingProtectedRoute>
                        <CoachingAdminLayout>
                          <GrievanceManagement />
                        </CoachingAdminLayout>
                      </CoachingProtectedRoute>
                  } />
                  
                  <Route path="/coaching-admin/leave" element={
                      <CoachingProtectedRoute>
                        <CoachingAdminLayout>
                          <CoachingLeaveManagement />
                        </CoachingAdminLayout>
                      </CoachingProtectedRoute>
                  } />
                  
                  <Route path="/coaching-admin/subscription" element={
                      <CoachingProtectedRoute>
                        <CoachingAdminLayout>
                          <SubscriptionManagement />
                        </CoachingAdminLayout>
                      </CoachingProtectedRoute>
                  } />
                  
                  {/* Admissions Management Routes */}
                  <Route path="/coaching-admin/admissions" element={
                      <CoachingProtectedRoute>
                        <CoachingAdminLayout>
                          <AdmissionsDashboard />
                        </CoachingAdminLayout>
                      </CoachingProtectedRoute>
                  } />
                  
                  <Route path="/coaching-admin/admissions/profile" element={
                      <CoachingProtectedRoute>
                        <CoachingAdminLayout>
                          <AdmissionsProfile />
                        </CoachingAdminLayout>
                      </CoachingProtectedRoute>
                  } />
                  
                  <Route path="/coaching-admin/admissions/form" element={
                      <CoachingProtectedRoute>
                        <CoachingAdminLayout>
                          <ApplicationFormBuilder />
                        </CoachingAdminLayout>
                      </CoachingProtectedRoute>
                  } />
                  
                  <Route path="/coaching-admin/admissions/curriculum" element={
                      <CoachingProtectedRoute>
                        <CoachingAdminLayout>
                          <CurriculumManagement />
                        </CoachingAdminLayout>
                      </CoachingProtectedRoute>
                  } />
                  
                  <Route path="/coaching-admin/admissions/materials" element={
                      <CoachingProtectedRoute>
                        <CoachingAdminLayout>
                          <MaterialsManagement />
                        </CoachingAdminLayout>
                      </CoachingProtectedRoute>
                  } />
                  
                  <Route path="/coaching-admin/admissions/faculty" element={
                      <CoachingProtectedRoute>
                        <CoachingAdminLayout>
                          <FacultyManagement />
                        </CoachingAdminLayout>
                      </CoachingProtectedRoute>
                  } />
                  
                  <Route path="/coaching-admin/admissions/applications" element={
                      <CoachingProtectedRoute>
                        <CoachingAdminLayout>
                          <ViewApplications />
                        </CoachingAdminLayout>
                      </CoachingProtectedRoute>
                  } />
                  
                  <Route path="/coaching-admin/admissions/fee-structure" element={
                      <CoachingProtectedRoute>
                        <CoachingAdminLayout>
                          <FeeStructureManagement />
                        </CoachingAdminLayout>
                      </CoachingProtectedRoute>
                  } />
                  
                  <Route path="/coaching-admin/admissions/tests" element={
                      <CoachingProtectedRoute>
                        <CoachingAdminLayout>
                          <AdmissionTestsList />
                        </CoachingAdminLayout>
                      </CoachingProtectedRoute>
                  } />
                  
                  <Route path="/coaching-admin/admissions/tests/:testId" element={
                      <CoachingProtectedRoute>
                        <CoachingAdminLayout>
                          <AdmissionTestEdit />
                        </CoachingAdminLayout>
                      </CoachingProtectedRoute>
                  } />
                  
                  <Route path="/coaching-admin/admissions/migrate-tests" element={
                      <CoachingProtectedRoute>
                        <CoachingAdminLayout>
                          <MigrateTestData />
                        </CoachingAdminLayout>
                      </CoachingProtectedRoute>
                  } />
                  
                  <Route path="/coaching-admin/admissions/results" element={
                      <CoachingProtectedRoute>
                        <CoachingAdminLayout>
                          <ResultsManagement />
                        </CoachingAdminLayout>
                      </CoachingProtectedRoute>
                  } />
                  
                  <Route path="/coaching-admin/fees" element={
                      <CoachingProtectedRoute>
                        <CoachingAdminLayout>
                          <FeeManagement />
                        </CoachingAdminLayout>
                      </CoachingProtectedRoute>
                  } />
                  
                  <Route path="/coaching-admin/report-cards" element={
                      <CoachingProtectedRoute>
                        <CoachingAdminLayout>
                          <ReportCards />
                        </CoachingAdminLayout>
                      </CoachingProtectedRoute>
                  } />
                  
                  <Route path="/coaching-admin/staff" element={
                      <CoachingProtectedRoute>
                        <CoachingAdminLayout>
                          <CoachingStaffManagement />
                        </CoachingAdminLayout>
                      </CoachingProtectedRoute>
                  } />
                  
                  <Route path="/coaching-admin/analytics" element={
                      <CoachingProtectedRoute>
                        <CoachingAdminLayout>
                          <DataAnalytics />
                        </CoachingAdminLayout>
                      </CoachingProtectedRoute>
                  } />
                  
                  <Route path="/coaching-admin/id-cards" element={
                      <CoachingProtectedRoute>
                        <CoachingAdminLayout>
                          <IDCardManagement />
                        </CoachingAdminLayout>
                      </CoachingProtectedRoute>
                  } />
                  
                  <Route path="/coaching-admin/store" element={
                      <CoachingProtectedRoute>
                        <CoachingAdminLayout>
                          <StoreManagementPage />
                        </CoachingAdminLayout>
                      </CoachingProtectedRoute>
                  } />
                  
                  <Route path="/coaching-admin/hostel" element={
                      <CoachingProtectedRoute>
                        <CoachingAdminLayout>
                          <HostelManagement />
                        </CoachingAdminLayout>
                      </CoachingProtectedRoute>
                  } />
                  
                  {/* Unschooling Mode Routes - Coming Soon */}
                  <Route path="/unschooling" element={
                      <ProtectedRoute>
                        <ComingSoon />
                      </ProtectedRoute>
                  } />
                  
                  <Route path="/unschooling/explore" element={
                      <ProtectedRoute>
                        <ComingSoon />
                      </ProtectedRoute>
                  } />
                  
                  <Route path="/unschooling/content/:category/:topic" element={
                      <ProtectedRoute>
                        <ComingSoon />
                      </ProtectedRoute>
                  } />
                  
                  <Route path="/unschooling/report" element={
                      <ProtectedRoute>
                        <ComingSoon />
                      </ProtectedRoute>
                  } />
                  
                  </Routes>
                        </SubdomainRouter>
                      </RoleBasedRedirect>
                    <NegotiationWidget />
                    </div>
                    </ProctoringProvider>
                </RealtimeProvider>
              </TestProvider>
            </ExamProvider>
          </UserDataProvider>
        </Router>
      </LanguageProvider>
      </HelmetProvider>
    </ErrorBoundary>
    </SentryErrorBoundary>
  );
}

export default App;
